import { Platform, StyleSheet } from "react-native";

export const font = (w: "400" | "500" | "600" | "700") =>
  Platform.select({ web: { fontFamily: "Inter, sans-serif", fontWeight: w }, default: {} });

const cardShadow = Platform.select({
  web: { boxShadow: "0px 0px 24px rgba(0,94,192,0.08)" } as any,
  default: {
    shadowColor: "#005EC0",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 3,
  },
});

export const styles = StyleSheet.create({
  // ── Layout (.cat-config-layout) : 3-column grid on web/tablet, stacked on narrow screens ──
  layout: {
    flexDirection: "row",
    gap: 16,
    alignItems: "flex-start",
  },
  layoutStacked: {
    flexDirection: "column",
  },
  panelFlexColumn: { flex: 1, minWidth: 0 },
  panelFlexStacked: { width: "100%" },

  // ── Panel card (.cc-panel) ──────────────────────────────────────────────────
  panel: {
    backgroundColor: "#fff",
    borderRadius: 10,
    overflow: "hidden",
    ...(cardShadow as object),
    minHeight: 360,
  },

  // ── Panel header (.cc-panel-header) ─────────────────────────────────────────
  panelHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
  },
  panelHeading: { minWidth: 0, flex: 1 },
  panelTitleRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  panelTitle: { fontSize: 14, color: "#2D2D2D", ...font("700") },
  panelCount: {
    minWidth: 20,
    height: 20,
    paddingHorizontal: 6,
    borderRadius: 10,
    backgroundColor: "#F8F8F8",
    alignItems: "center",
    justifyContent: "center",
    ...font("700"),
  },
  panelCountText: { fontSize: 11, color: "#585858", ...font("700") },
  panelContext: {
    fontSize: 12,
    color: "#656565",
    marginTop: 3,
    ...font("500"),
  },

  // Add button (.btn-secondary.btn-sm)
  btnSm: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#034895",
    backgroundColor: "#fff",
    flexShrink: 0,
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
  },
  btnSmText: { fontSize: 12, color: "#034895", ...font("700") },
  btnSmDisabled: { opacity: 0.4 },

  // ── Panel body / list (.cc-panel-body) ──────────────────────────────────────
  panelBody: { padding: 8, flexGrow: 1 },
  panelBodyScroll: { maxHeight: 460 },

  emptyPanel: { alignItems: "center", justifyContent: "center", paddingVertical: 40, paddingHorizontal: 16, gap: 8 },
  emptyPanelText: { fontSize: 13, color: "#818181", textAlign: "center", ...font("400") },

  // List item row (category / subcategory / tertiary entries)
  listItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
    marginBottom: 2,
  },
  listItemActive: { backgroundColor: "#E8F0FB" },
  listItemHovered: { backgroundColor: "#F3F7FD" },
  listItemInactive: { opacity: 0.5 },
  listItemIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: "#F3F7FD",
    alignItems: "center",
    justifyContent: "center",
  },
  listItemTextWrap: { flex: 1, minWidth: 0 },
  listItemLabel: { fontSize: 13, color: "#2D2D2D", ...font("600") },
  listItemLabelActive: { color: "#034895" },
  listItemSubLabel: { fontSize: 11, color: "#818181", marginTop: 1, ...font("400") },
  listItemCount: {
    minWidth: 22,
    height: 20,
    paddingHorizontal: 5,
    borderRadius: 10,
    backgroundColor: "#F0F0F0",
    alignItems: "center",
    justifyContent: "center",
  },
  listItemCountText: { fontSize: 10, color: "#656565", ...font("700") },
  addFieldsRowButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: "#D9D9D9",
    borderRadius: 100,
    backgroundColor: "#fff",
    flexShrink: 0,
  },
  addFieldsRowButtonText: { fontSize: 11, color: "#034895", ...font("700") },
  listItemActions: { flexDirection: "row", alignItems: "center", gap: 4 },
  listItemActionBtn: { padding: 4 },

  // ── Existing fields list (GET /ticketing/config/fields), shown under a selected row ──
  existingFieldsWrap: {
    marginLeft: 10,
    marginRight: 10,
    marginBottom: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#F8FAFD",
    borderWidth: 1,
    borderColor: "#EBEEF2",
  },
  existingFieldsHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 8,
  },
  existingFieldsHeaderText: { fontSize: 11, color: "#656565", ...font("700") },
  existingFieldsHeaderCount: {
    minWidth: 20,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 100,
    backgroundColor: "#E8F0FB",
    color: "#034895",
    fontSize: 10,
    ...font("700"),
  },
  existingFieldsEmptyText: { fontSize: 11, color: "#818181", ...font("400") },
  existingFieldPressable: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5EAF0",
    marginBottom: 8,
  },
  existingFieldHeader: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 6 },
  existingFieldTitle: { fontSize: 12, color: "#2D2D2D", ...font("600"), flexShrink: 1 },
  existingFieldBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 100,
    backgroundColor: "#E8F0FB",
  },
  existingFieldRequiredBadge: { backgroundColor: "#FFF1E8" },
  existingFieldBadgeText: { fontSize: 10, color: "#034895", ...font("700") },
  existingFieldInactiveBadge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 100, backgroundColor: "#F0F0F0" },
  existingFieldInactiveBadgeText: { fontSize: 10, color: "#656565", ...font("700") },
  existingFieldName: { marginTop: 4, fontSize: 11, color: "#818181", ...font("500") },
  existingFieldMetaRow: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 4 },
  existingFieldMetaText: { fontSize: 10, color: "#656565", ...font("500") },
  existingFieldDetailText: { marginTop: 4, fontSize: 10, color: "#818181", ...font("400") },
  existingFieldOptions: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 6 },
  existingFieldOptionChip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 100,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#D9D9D9",
  },
  existingFieldOptionChipText: { fontSize: 10, color: "#585858", ...font("500") },
  // ── Toggle switch — identical tokens to TeamAssignment.styles.ts ──
  toggleTrack: {
    width: 36,
    height: 20,
    borderRadius: 20,
    justifyContent: "center",
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as any) : {}),
  },
  toggleTrackOn:  { backgroundColor: "#005CBC" },
  toggleTrackOff: { backgroundColor: "#D0D0D0" },
  toggleThumb: {
    position: "absolute",
    left: 2,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: "#ffffff",
    ...Platform.select({
      web: { boxShadow: "0 1px 3px rgba(0,0,0,0.15)" } as any,
      default: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.15,
        shadowRadius: 3,
        elevation: 2,
      },
    }),
  },
});

export default styles;
